// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getSelectedProductPath, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getComputedVariantProduct,
    getProductsDataView,
    getProductState,
    getProductStateByPath,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGAUpdate } from "@rc/App/Handlers/GoogleAnalytics/GA/xGA";
import { CustomAppState, xGaState } from "@rc/App/Store/Reducers";
import { getDataLayer, rcBrand } from "@rc/XDL/Utilities/DataLayer";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    let PDPProduct: any = null;
    let PLPProducts: any = [];
    let cart: any = null;
    const billToState = getBillToState(state, state.context.session.billToId);
    const shipToState = getShipToState(state, state.context.session.shipToId);
    const siteEnvironmentType = (getSettingsCollection(state).customSetting as any)?.siteEnvironmentType;
    const pageTheme = (getSettingsCollection(state).customSetting as any)?.pageTheme;
    const pageType = (getSettingsCollection(state).customSetting as any)?.pageType;

    let shipTo;
    if (shipToState && shipToState.value) {
        shipTo = shipToState.value;
    }

    let billTo;
    if (billToState && billToState.value) {
        billTo = billToState.value;
    }

    const currentPage = getCurrentPage(state).type;
    const location = getLocation(state);
    const isPDP = currentPage === "ProductDetailsPage";
    const isPLP = currentPage === "ProductListPage";
    const isCartPage = currentPage === "CartPage";
    const isCheckoutReviewAndSubmitPage = currentPage === "CheckoutReviewAndSubmitPage";

    if (isPDP) {
        const productPath =
            getSelectedProductPath(state) ||
            (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
        const productState = getProductStateByPath(state, productPath);
        const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
        const computedVariantProduct = getComputedVariantProduct(productState, variantProductState);
        const productInfo =
            computedVariantProduct.value && state.pages.productDetails.productInfosById
                ? state.pages.productDetails.productInfosById[computedVariantProduct.value.id]
                : undefined;

        if (productInfo?.pricing) {
            PDPProduct = { pricing: productInfo?.pricing, ...productState.value };
        }
    }

    if (isPLP) {
        const {
            pages: {
                productList: { parameter },
            },
        } = state;
        const productsDataView = getProductsDataView(state, parameter);
        if (productsDataView && productsDataView.value) {
            PLPProducts = [];
            productsDataView.value.forEach(product => {
                const productInfo = state.pages.productList.productInfosByProductId[product.id];
                if (productInfo?.pricing) {
                    PLPProducts.push({ ...product, pricing: productInfo?.pricing });
                }
            });
        }
    }

    cart = getCurrentCartState(state);

    return {
        currentPage,
        PDPProduct,
        PLPProducts,
        cart,
        xGAState: state.xGA,
        currencyCode: state.context?.session?.currency?.currencyCode,
        isPDP,
        isPLP,
        isCartPage,
        isCheckoutReviewAndSubmitPage,
        location,
        session: state.context.session,
        shipTo,
        billTo,
        siteEnvironmentType,
        pageTheme,
        pageType,
    };
};

const mapDispatchToProps = {
    xGAUpdate,
};

const enum fields {
    checkoutFlowEvents = "checkoutFlowEvents",
    addToCartEvent = "addToCartEvent",
    breederRecommendationPortalEvent = "breederRecommendationPortalEvent",
    removeCartEvent = "removeCartEvent",
    checkoutFunnelEvent = "checkoutFunnelEvent",
    transactionEvent = "transactionEvent",
    removeCartLineEvent = "removeCartLineEvent",
    purchaseEvent = "purchaseEvent",
    removeFromCartLineEvent = "removeFromCartLineEvent",
    pdpViewEvent = "pdpViewEvent",
    productImpressionEvent = "productImpressionEvent",
    selectPromotionEvent = "selectPromotionEvent",
    viewCartEvent = "viewCartEvent",
    contactUsEvent = "contactUsEvent",
    productClickEvent = "productClickEvent",
    includeProductCategory = "includeProductCategory",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.checkoutFlowEvents]: boolean;
        [fields.addToCartEvent]: boolean;
        [fields.breederRecommendationPortalEvent]: boolean;
        [fields.removeCartEvent]: boolean;
        [fields.checkoutFunnelEvent]: boolean;
        [fields.transactionEvent]: boolean;
        [fields.removeCartLineEvent]: boolean;
        [fields.purchaseEvent]: boolean;
        [fields.removeFromCartLineEvent]: boolean;
        [fields.pdpViewEvent]: boolean;
        [fields.productImpressionEvent]: boolean;
        [fields.selectPromotionEvent]: boolean;
        [fields.viewCartEvent]: boolean;
        [fields.contactUsEvent]: boolean;
        [fields.productClickEvent]: boolean;
        [fields.includeProductCategory]: boolean;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface GoogleAnalyticsWidgetStyles {
    wrapper?: InjectableCss;
    instructionText?: TypographyPresentationProps;
}

export const googleAnalyticsWidgetStyles: GoogleAnalyticsWidgetStyles = {
    wrapper: {
        css: css`
            padding: 10px;
            background-color: ${getColor("secondary.main")};
        `,
    },
    instructionText: {
        color: "primary.contrast",
        variant: "h6",
        css: css`
            margin: 0;
        `,
    },
};

const GoogleAnalyticsWidget: FC<Props> = ({
    xGAUpdate,
    fields,
    currentPage,
    PDPProduct,
    PLPProducts,
    cart,
    xGAState,
    currencyCode,
    shellContext,
    isPDP,
    isPLP,
    isCartPage,
    isCheckoutReviewAndSubmitPage,
    location,
    session,
    shipTo,
    billTo,
    siteEnvironmentType,
    pageTheme,
    pageType,
}) => {
    const styles = googleAnalyticsWidgetStyles;
    if (shellContext.isInShell && shellContext.isEditing) {
        // Placeholder in CMS view
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.instructionText}>{translate("Google Analytics Widget (GA4)")}</Typography>
            </StyledWrapper>
        );
    }
    const [PDPInfoLoaded, setPDPInfoLoaded] = useState<boolean>(false);
    const [PLPInfoLoaded, setPLPInfoLoaded] = useState<boolean>(false);

    const customerTypeDictionary = {
        BRDC: "Breeder",
        BRDF: "Breeder",
        RSH: "Rescue/Shelter",
        PFP: "Working Dog",
        VET: "Veterinary",
        CFP: "Clinic Feeding",
        BFE: "Banfield Associate",
        MVH: "Mars Veterinary Health",
        KOL: "Board-Cert Vet/Resident",
        UFP: "University Feeding",
        RCE: "Royal Canin Associate",
    } as any;

    useEffect(() => {
        xGAUpdate({
            checkoutFlowEvents: fields["checkoutFlowEvents"] || false,
            addToCartEvent: fields["addToCartEvent"] || false,
            breederRecommendationPortalEvent: fields["breederRecommendationPortalEvent"] || false,
            removeCartEvent: fields["removeCartEvent"] || false,
            checkoutFunnelEvent: fields["checkoutFunnelEvent"] || false,
            transactionEvent: fields["transactionEvent"] || false,
            removeCartLineEvent: fields["removeCartLineEvent"] || false,
            purchaseEvent: fields["purchaseEvent"] || false,
            removeFromCartLineEvent: fields["removeFromCartLineEvent"] || false,
            pdpViewEvent: fields["pdpViewEvent"] || false,
            productImpressionEvent: fields["productImpressionEvent"] || false,
            selectPromotionEvent: fields["selectPromotionEvent"] || false,
            viewCartEvent: fields["viewCartEvent"] || false,
            contactUsEvent: fields["contactUsEvent"] || false,
            productClickEvent: fields["productClickEvent"] || false,
            includeProductCategory: fields["includeProductCategory"] || false,
        });
    }, [fields]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (dataLayer) {
            if (isPDP && xGAState.pdpViewEvent && PDPProduct && !PDPInfoLoaded) {
                setPDPInfoLoaded(true);

                let shortDescription = PDPProduct?.productTitle;
                shortDescription = shortDescription.replace(/<(?:.|\n)*?>/gm, "");
                shortDescription = shortDescription.replace(/\s+/g, " ").trim();

                const lifestageValue = PDPProduct.properties?.lifeStage;
                const benefitsValue = PDPProduct.properties?.benefits;
                const category = PDPProduct.properties?.category;
                const variant = PDPProduct.properties?.variant;

                const productLocation = localStorage.getItem("productLocation");

                const GA4Data = {
                    event: "US001eComProductView",
                    ecommerce: {
                        currencyCode: "USD",
                        detail: {
                            actionField: {},
                            products: [
                                {
                                    name: shortDescription,
                                    id: PDPProduct.productNumber,
                                    price: PDPProduct.pricing.unitNetPrice,
                                    brand: rcBrand,
                                },
                            ],
                        },
                    },
                } as any;

                if (xGAState["includeProductCategory"]) {
                    GA4Data.ecommerce.detail.products[0]["lifeStage"] = lifestageValue || "";
                    GA4Data.ecommerce.detail.products[0]["benefits"] = benefitsValue || "";
                    GA4Data.ecommerce.detail.products[0]["category"] = category || "";
                    GA4Data.ecommerce.detail.products[0]["variant"] = variant || "";
                }

                if (productLocation) {
                    GA4Data.ecommerce.detail.actionField = {
                        list: productLocation,
                    };
                    localStorage.removeItem("productLocation");
                }

                dataLayer.push(GA4Data);
            }

            if (isPLP && xGAState.productImpressionEvent && PLPProducts?.length && !PLPInfoLoaded) {
                setPLPInfoLoaded(true);
                const GA4Data = {
                    event: "view_item_list",
                    items: [] as any,
                } as any;

                PLPProducts.forEach((product: any, idx: number) => {
                    const GA4temp: any = {
                        item_name: product?.productTitle,
                        item_id: product?.productNumber,
                        price: product.pricing?.unitNetPrice,
                        index: idx,
                    };

                    if (xGAState["includeProductCategory"]) {
                        GA4temp["item_category"] = product.properties["category"] || "";
                        GA4temp["item_list_id"] = product.properties["categoryId"] || "";
                    }
                    GA4Data.items.push(GA4temp);
                });

                dataLayer.push(GA4Data);
            }
        }
    }, [PLPProducts, PDPProduct, currentPage, xGAState]);

    useEffect(() => {
        setPDPInfoLoaded(false);
        setPLPInfoLoaded(false);

        const dataLayer = getDataLayer();

        if (!cart || !cart.value || cart.isLoading) {
            return;
        }

        if (isCartPage && xGAState.viewCartEvent) {
            const GA4Data = {
                event: "view_cart",
                value: cart.value.orderSubTotal,
                currency: currencyCode,
                items: [],
            } as any;

            cart.value?.cartLines?.forEach((cartItem: any, idx: number) => {
                const GA4Item: any = {
                    item_name: cartItem.productName,
                    item_id: cartItem.productId,
                    price: cartItem.pricing?.unitNetPrice,
                    quantity: cartItem.qtyOrdered,
                    index: idx,
                };

                if (xGAState["includeProductCategory"]) {
                    GA4Item["item_category"] = cartItem.properties["category"] || "";
                    GA4Item["item_list_id"] = cartItem.properties["categoryId"] || "";
                }
                GA4Data.items.push(GA4Item);
            });

            dataLayer.push(GA4Data);
        }

        if (isCheckoutReviewAndSubmitPage && xGAState.checkoutFunnelEvent) {
            const GA4Data = {
                event: "US001eComCheckout",
                ecommerce: {
                    checkout: {
                        actionField: {
                            step: 2,
                        },
                        products: [],
                    },
                },
            } as any;

            const cartLines = cart.value?.cartLines;

            cartLines?.forEach((cartLine: any, index: number) => {
                let shortDescription = cartLine.shortDescription.replace(/<(?:.|\n)*?>/gm, "");
                shortDescription = shortDescription.replace(/\s+/g, " ").trim();

                const lifestageValue = cartLine.properties?.lifeStage;
                const benefitsValue = cartLine.properties?.benefits;
                const category = cartLine.properties?.category;
                const variant = cartLine.properties?.variant;

                const product = {
                    id: cartLine.erpNumber,
                    price: cartLine.pricing.unitNetPrice?.toString(),
                    name: shortDescription,
                    brand: rcBrand,
                    orderingType: localStorage.getItem("orderingType"),
                    quantity: cartLine?.qtyOrdered,
                } as any;

                if (xGAState["includeProductCategory"]) {
                    product["lifeStage"] = lifestageValue || "";
                    product["benefits"] = benefitsValue || "";
                    product["category"] = category || "";
                    product["variant"] = variant || "";
                }

                GA4Data.ecommerce.checkout.products.push(product);
            });

            dataLayer.push(GA4Data);
        }
    }, [currentPage, cart]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (dataLayer && billTo && shipTo) {
            // be sure that the rc event is not pushed twice
            const rcPageViewEvents = dataLayer.filter((item: any) => item.event === "virtualPageView");
            if (rcPageViewEvents[rcPageViewEvents.length - 1]?.page?.virtualPageURL === location.pathname) {
                return;
            }

            let pageCategory = "";
            if (isPDP) {
                pageCategory = PDPProduct?.properties?.["pageCategory"];
            } else if (isPLP) {
                pageCategory = PLPProducts[0]?.properties?.["pageCategory"];
            }

            const GA4Data = {
                event: "rcVirtualPageView",
                user: {},
                customer: {
                    id: billTo && billTo.customerNumber,
                    shippingId: shipTo && shipTo.id,
                    classification: shipTo && shipTo.properties && shipTo.properties["veT_Classification"],
                    name: billTo && billTo.companyName,
                },
                page: {
                    theme: pageTheme,
                    type: pageType,
                    hitTimestamp: new Date(),
                    virtualPageURL: location?.pathname,
                    virtualPageTitle: document?.title,
                    category: pageCategory,
                },
                product: {
                    name: "",
                    id: "",
                    category: "",
                },
                pet: {
                    specieId: "",
                    breedId: "",
                },
                site: {
                    id: "US001",
                    environment: siteEnvironmentType,
                    country: "US",
                    currency: "USD",
                },
            } as any;

            if (session && session.isAuthenticated) {
                const customerType = billTo?.properties["customerType"] || shipTo?.properties["customerType"];
                const accountType = session.userRoles && session.userRoles.indexOf("CSR") !== -1 ? "test" : "regular";
                const userId = session.userProfileId;

                GA4Data.user = {
                    id: userId,
                    country: billTo && billTo.country && billTo.country.abbreviation,
                    locale: session?.language?.languageCode,
                    type: cart?.value?.properties?.customerType,
                    crownPartner: "",
                    accountType,
                    authenticationStatus: "authenticated",
                };
            } else {
                GA4Data.user = {
                    authenticationStatus: "not authenticated",
                };
            }

            dataLayer.push(GA4Data);
        }
    }, [currentPage, billTo, shipTo]);

    return null;
};

const eventsTab = {
    displayName: "Events",
    sortOrder: 1,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(GoogleAnalyticsWidget)),
    definition: {
        displayName: "Google Analytics Management (GA4)",
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.addToCartEvent,
                displayName: "Add To Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.transactionEvent,
                displayName: "Transaction (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.checkoutFunnelEvent,
                displayName: "Checkout Funnel (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.removeCartEvent,
                displayName: "Remove Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.removeCartLineEvent,
                displayName: "Remove Cart Line (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.removeFromCartLineEvent,
                displayName: "Remove Cartline (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.pdpViewEvent,
                displayName: "PDP View (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.productImpressionEvent,
                displayName: "Product Impression (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.viewCartEvent,
                displayName: "View Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.contactUsEvent,
                displayName: "ContactUs (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.productClickEvent,
                displayName: "Product Click (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.breederRecommendationPortalEvent,
                displayName: "Breeder Recommendation Portal (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.purchaseEvent,
                displayName: "Purchase (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.checkoutFlowEvents,
                displayName: "Checkout Flow (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.selectPromotionEvent,
                displayName: "Select Promotion Event",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.includeProductCategory,
                displayName: "Include Product Category (data)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: settingsTab,
            },
        ],
    },
};

export default widgetModule;
